import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router';
import { authApi } from '@/auth/authApi';
import { useAuthStore } from '@/auth/authStore';
import { useColorMode } from '@/app/colorMode';
import { useEnumLabel } from '@/shared/hooks/useEnumLabel';
import { LanguageSwitcher } from './LanguageSwitcher';
import { SideMenu } from './SideMenu';

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED = 72;
const COLLAPSE_KEY = 'aml.menuCollapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell() {
  const { t } = useTranslation(['common', 'menu']);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const { mode, toggle } = useColorMode();
  const enumLabel = useEnumLabel();

  const width = isDesktop ? (collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH) : DRAWER_WIDTH;

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });

  const logout = async () => {
    setUserMenu(null);
    await authApi.logout().catch(() => undefined);
    clear();
    navigate('/login', { replace: true });
  };

  const drawer = (
    <>
      <Toolbar sx={{ gap: 1.5, px: 2 }}>
        <Box component="img" src="/favicon.svg" alt="" sx={{ width: 28, height: 28 }} />
        {(!collapsed || !isDesktop) && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }} noWrap>
              {t('common:appName')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user?.tenant.name}
            </Typography>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <SideMenu collapsed={isDesktop && collapsed} onNavigate={isDesktop ? undefined : () => setMobileOpen(false)} />
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{ width: { md: `calc(100% - ${width}px)` }, ml: { md: `${width}px` }, borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Tooltip title={t(collapsed ? 'menu:expand' : 'menu:collapse')}>
            <IconButton edge="start" onClick={isDesktop ? toggleCollapsed : () => setMobileOpen(true)} aria-label={t('menu:expand')}>
              {isDesktop && !collapsed ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <LanguageSwitcher />
          <Tooltip title={t(mode === 'light' ? 'common:theme.dark' : 'common:theme.light')}>
            <IconButton onClick={toggle}>{mode === 'light' ? <DarkModeOutlined fontSize="small" /> : <LightModeOutlined fontSize="small" />}</IconButton>
          </Tooltip>
          <IconButton onClick={(e) => setUserMenu(e.currentTarget)} aria-label={user?.displayName}>
            <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>
              {user?.displayName.slice(0, 1).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu anchorEl={userMenu} open={!!userMenu} onClose={() => setUserMenu(null)}>
            <Stack sx={{ px: 2, py: 1, minWidth: 220 }}>
              <Typography variant="subtitle2">{user?.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.username} · {user?.tenant.slug}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.roles.map((r) => enumLabel('Role', r)).join(', ')}
              </Typography>
            </Stack>
            <Divider />
            <MenuItem onClick={logout}>
              <ListItemIcon>
                <LogoutOutlined fontSize="small" />
              </ListItemIcon>
              {t('common:actions.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="aside" sx={{ width: { md: width }, flexShrink: { md: 0 }, transition: 'width 150ms' }}>
        {isDesktop ? (
          <Drawer
            variant="permanent"
            open
            slotProps={{ paper: { sx: { width, overflowX: 'hidden', transition: 'width 150ms', display: 'flex', flexDirection: 'column' } } }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box component="main" sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 }, pt: { xs: 10, md: 11 } }}>
        <Outlet />
      </Box>
    </Box>
  );
}
